import React, { useEffect, useState } from 'react';
import CreateNewModalCoreComponent, {
  ICohortDetails,
} from '../CreateNewModalCoreComponent';
import componentConstants from '../../../core-utils/messages/componentConstants';
import { CSSProperties } from '@mui/material/styles';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { TextField } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import { useStores } from '../../../stores';
import {
  Reports,
  allReports,
} from '../../../core-utils/helper-utils/reportsWorkFlowHelper';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import moment from 'moment';
import Snackbar from '../../atoms/Snackbar';
import { InitialDates } from '../../molecules/NewEditDuration';
import { IEmployerGroupResponseData } from '../../../pages/HomePage';
import { toJS } from 'mobx';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { getDateFromMonthAndYear } from '../../../core-utils/helper-utils/helper';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  UserAccessConstants,
  checkUserAccess,
} from '../../../core-utils/helper-utils/userAccess';
import FeatureAccessModal from '../FeatureAccessModal';
import {
  EmailSchedulerErrorType,
  EmailSchedulerType,
  emailSchedulerErrorDefaultObject,
  getEmailSchedulerRequestBody,
} from '../../../core-utils/helper-utils/emailSchedulerHelper';
import EmailScheduler from '../EmailScheduler';

export interface CreateNewInternalReportsModalProps {
  open: boolean;
  handleClose: () => void;
  handleSave: () => void;
}

const CreateNewInternalReportsModal: React.FC<CreateNewInternalReportsModalProps> = ({
  open,
  handleClose,
  handleSave,
}) => {
  const useStyles = makeStyles()({
    infoItem: {
      background: patientAccessTheme.palette.AIQChips?.infoBackground,
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      height: '37px',
      width: '616px',
      marginTop: '12px',
    },
    addCohortInfo: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'break-spaces',
    },
  });

  const { classes } = useStyles();
  const constants =
    componentConstants.ORGANISMS.REPORTS_CREATE_NEW_MODAL.CREATE_NEW_REPORT;

  const [cohortDetailsList, setCohortDetailsList] = useState<ICohortDetails[]>(
    [],
  );
  const employerGroupStore = useStores().employerGroupStore;
  const loginStore = useStores().loginPageStore;
  const customReportsStore = useStores().customReportsStore;
  const [openFeatureAccessModal, setOpenFeatureAccessModal] = useState(false);
  const [isYearSelection, setIsYearSelection] = useState(false);
  const [reportName, setReportName] = useState<string>(
    toJS(customReportsStore.reportName),
  );
  const [selectedReports, setSelectedReports] = useState<Reports[]>(
    toJS(customReportsStore.reportsData),
  );
  const [selectedReportsIds, setSelectedReportsIds] = useState<string[]>([]);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [downloadFrequency, setDownloadFrequency] = useState<string>(
    toJS(customReportsStore.downloadfrequency),
  );
  const [
    emailSchedulerData,
    setEmailSchedulerData,
  ] = useState<EmailSchedulerType>(toJS(customReportsStore.emailSchedulerData));
  const [
    emailSchedulerError,
    setEmailSchedulerError,
  ] = useState<EmailSchedulerErrorType>(emailSchedulerErrorDefaultObject);
  const [schedulerChecked, setSchedulerChecked] = useState(true);

  const employerGroupData: IEmployerGroupResponseData =
    employerGroupStore.employerGroupResponseData;
  const planPeriodDates: InitialDates = {
    start_month: employerGroupData.start_month,
    end_month: employerGroupData.end_month,
    plan_start_month: employerGroupData.plan_start_month,
    start_year: employerGroupData.start_year,
    end_year: employerGroupData.end_year,
    plan_end_month: employerGroupData.plan_end_month,
    recent_plan_year: employerGroupData.recent_plan_year,
  };

  const {
    scheduledDate,
    scheduledDay,
    scheduledTime,
    schedulerStartDate,
    schedulerEndDate,
    toEmails,
  } = emailSchedulerData;

  const requestBody = {
    name: reportName,
    employerGroupIds: employerGroupStore.employerGroupResponseData.eg_nid,
    selectedReportIds: selectedReportsIds,
    downloadFrequency: downloadFrequency,
    startMonth: moment(startDate).format('MMM'),
    startYear: moment(startDate).format('YYYY'),
    endMonth: moment(endDate).format('MMM'),
    endYear: moment(endDate).format('YYYY'),
    createdBy: loginStore.userId,
    emailSchedulerDTO: schedulerChecked
      ? getEmailSchedulerRequestBody(
          scheduledDate,
          scheduledDay,
          scheduledTime,
          schedulerStartDate,
          schedulerEndDate,
          toEmails,
          loginStore.email,
          downloadFrequency,
        )
      : null,
  };

  const isEmailSchedulerFilled = Boolean(
    schedulerChecked
      ? (downloadFrequency === 'Custom'
          ? scheduledDate
          : scheduledDay && schedulerStartDate && schedulerEndDate) &&
          scheduledTime &&
          toEmails?.length !== 0 &&
          Object?.values(emailSchedulerError).every((value) => value === false)
      : true,
  );

  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleReportName = (event: any) => {
    const value = event.target.value as string;
    setReportName(value);
  };

  const handleReports = (event: any, value: any) => {
    setSelectedReports(value);
  };

  const validate = () => {
    reportName !== '' &&
    selectedReports?.length > 0 &&
    downloadFrequency !== '' &&
    isEmailSchedulerFilled
      ? setIsSaveButtonDisabled(false)
      : setIsSaveButtonDisabled(true);
  };

  const getSelectedReportIds = () => {
    setSelectedReportsIds(
      selectedReports?.map((report) => {
        return report.id;
      }),
    );

    //to check download frequency of selected reports
    let isAnnual = false;
    selectedReports?.map((report) => {
      if (report?.downloadFrequency === 'Annual') {
        isAnnual = true;
      }
    });
    setIsYearSelection(isAnnual);
  };

  const getDatesForDownloadFrequency = () => {
    const planEndDate = getDateFromMonthAndYear(
      planPeriodDates.end_month,
      planPeriodDates.end_year,
      true,
    );

    const subtractMonths = (months: number) => {
      return moment(planEndDate)
        .subtract(months, 'months')
        .add(1, 'days')
        .format('YYYY-MM-DD');
    };
    setEndDate(planEndDate);
    switch (downloadFrequency) {
      case 'Monthly':
        setStartDate(subtractMonths(1));
        break;
      case 'Quarterly':
        setStartDate(subtractMonths(3));
        break;
      case 'Half-Yearly':
        setStartDate(subtractMonths(6));
        break;
      case 'Annually':
        setStartDate(subtractMonths(12));
        break;
      default:
        setStartDate('');
        setEndDate('');
    }
  };

  useEffect(() => {
    validate();
    getSelectedReportIds();
    getDatesForDownloadFrequency();
  }, [reportName, selectedReports, downloadFrequency]);

  useEffect(() => {
    validate();
  }, [emailSchedulerData, emailSchedulerError, schedulerChecked]);

  const saveCustomReport = async () => {
    await axios
      .post(
        properties.userManagementBaseUrl + properties.customReports,
        requestBody,
      )
      .then((response) => {
        if (response.status === 200) {
          handleClose();
          handleSave();
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const updateCustomReport = async () => {
    await axios
      .patch(
        properties.userManagementBaseUrl +
          properties.customReports +
          '/' +
          customReportsStore.id,
        requestBody,
      )
      .then((response) => {
        if (response.status === 200) {
          handleClose();
          handleSave();
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleOnclickSave = () => {
    customReportsStore.isEdit ? updateCustomReport() : saveCustomReport();
  };

  return (
    <>
      <CreateNewModalCoreComponent
        open={open}
        handleClose={handleClose}
        title={
          customReportsStore.isEdit ? constants.UPDATE_TITLE : constants.TITLE
        }
        subTitle={constants.SUBTITLE as [string, CSSProperties | undefined][]}
        reportCreateNewLabelFields={
          <>
            <ReportCreateNewLabelField
              label={constants.REPORT_NAME}
              component={
                <TextField
                  value={reportName}
                  placeholder={constants.REPORT_PLACEHOLDER}
                  height="36px"
                  width="301px"
                  inputPropsStyle={{
                    paddingLeft: '16px',
                  }}
                  onChange={(event: any) => handleReportName(event)}
                />
              }
              marginBottom="12px"
            />
            <ReportCreateNewLabelField
              label={constants.SELECT_REPORTS}
              component={
                <AutocompleteWithCheckBoxes
                  options={allReports}
                  handleChange={handleReports}
                  defaultValue={selectedReports}
                  placeholder={
                    selectedReports?.length === 0
                      ? constants.REPORTS_PLACEHOLDER
                      : ''
                  }
                />
              }
            />
            <ReportCreateNewLabelField
              label={constants.DOWNLOAD_FREQUENCY}
              component={
                <Dropdown
                  label="Select"
                  dropDownOptions={
                    isYearSelection
                      ? constants.ANNUAL_DROPDOWN_OPTIONS
                      : constants.DROPDOWN_OPTIONS
                  }
                  dropDownValues={
                    isYearSelection
                      ? constants.ANNUAL_DROPDOWN_OPTIONS
                      : constants.DROPDOWN_OPTIONS
                  }
                  value={downloadFrequency}
                  setValue={setDownloadFrequency}
                  selectStyles={{
                    ...commonDropDownProps.selectStyles,
                    width: '301px',
                  }}
                  labelTop="10px"
                  disabled={!(selectedReports?.length > 0)}
                />
              }
            />
          </>
        }
        infoGrid={
          downloadFrequency !== '' && downloadFrequency !== 'Custom' ? (
            <div className={classes.infoItem}>
              <div className={classes.addCohortInfo}>
                <Info
                  height={'16px'}
                  width="16px"
                  style={{ marginRight: '8px', marginLeft: '10px' }}
                />
                <Typography
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  fontSize="12px"
                  lineHeight="21.24px"
                  fontType="regular"
                >
                  {constants.DOWNLOAD_FREQUENCY_INFO.PART1 +
                    downloadFrequency?.toLocaleLowerCase() +
                    constants.DOWNLOAD_FREQUENCY_INFO.PART2 +
                    (downloadFrequency === 'Monthly'
                      ? moment(startDate).format('MMM YYYY')
                      : moment(startDate).format('MMM YYYY') +
                        '-' +
                        moment(endDate).format('MMM YYYY'))}
                </Typography>
              </div>
            </div>
          ) : (
            <></>
          )
        }
        emailScheduler={
          selectedReports?.length > 0 && downloadFrequency !== '' ? (
            <EmailScheduler
              isCustomType={downloadFrequency === 'Custom'}
              frequencyType={downloadFrequency}
              emailSchedulerData={emailSchedulerData}
              setEmailSchedulerData={setEmailSchedulerData}
              emailSchedulerError={emailSchedulerError}
              setEmailSchedulerError={setEmailSchedulerError}
              showInfoText={isEmailSchedulerFilled}
              checked={schedulerChecked}
              setChecked={setSchedulerChecked}
            />
          ) : (
            <></>
          )
        }
        showDuration={
          selectedReports?.length > 0 && downloadFrequency === 'Custom'
        }
        isYearSelection={isYearSelection}
        cohortState={[cohortDetailsList, setCohortDetailsList]}
        startDateUseState={[startDate, setStartDate]}
        endDateUseState={[endDate, setEndDate]}
        propertyDropDownOptions={[]}
        categoryDropDownOptions={[]}
        conditionDropDownOptions={[]}
        cohortSelectionsValues={[]}
        handleAddCohort={() => {}}
        handleDeleteCohort={() => {}}
        handleDownload={handleOnclickSave}
        isSaveButtonDisabled={isSaveButtonDisabled}
        initialDates={planPeriodDates}
        showAddCohorts={selectedReports?.length > 0}
        handleOnClickAddCohort={() => {
          if (
            properties.environment === 'dev' &&
            checkUserAccess(
              UserAccessConstants.COHORT_DETAILS,
              loginStore.userResponse.role.name,
              loginStore.userFeatures,
            )
          ) {
            //TODO Implement Cohort feature
          } else {
            setOpenFeatureAccessModal(true);
          }
        }}
      />
      {openFeatureAccessModal && (
        <FeatureAccessModal
          open={openFeatureAccessModal}
          handleClose={() => {
            setOpenFeatureAccessModal(false);
          }}
          access={checkUserAccess(
            UserAccessConstants.COHORT_DETAILS,
            loginStore.userResponse.role.name,
            loginStore.userFeatures,
          )}
        ></FeatureAccessModal>
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
};

export default CreateNewInternalReportsModal;
